import { Engine } from 'engine/Engine';
import { Transform } from 'engine/Transform';
import { Facet } from 'engine/facets/Facet';
import { SpriteGroupFacet } from 'engine/facets/SpriteGroupFacet';
import { StaticSpriteFacet } from 'engine/facets/StaticSpriteFacet';
import { AudioManager } from 'engine/lib/AudioManager';
import { V2, addV2, getAngleRad, scaleV2 } from 'engine/lib/V2';
import { create } from 'engine/lib/create';
import { BotArmsSprite, BotArmsWateringSprite } from 'game/sprites/BotArmsSprite';
import { LaserSprite } from 'game/sprites/LaserSprite';
import { BotState, BotToolChoice, ProcessStatus } from 'game/state/BotState';
import clamp from 'lodash/clamp';
import { BulletEntity } from '../BulletEntity';

const errorSoundPath = require('game/sfx/error.mp3');
const waterSoundPath = require('game/sfx/splash.mp3');

const errorSound = new AudioManager(errorSoundPath);
const waterAudio = new AudioManager(waterSoundPath, { loop: true });
const emptyArray: StaticSpriteFacet[] = [];

interface BotToolBehaviorProps {
  armsSprite: StaticSpriteFacet,
  toolSprite: SpriteGroupFacet,
  sprite: SpriteGroupFacet,
  result: ProcessStatus | null,
  state: BotState,
}

export class BotToolBehavior extends Facet<BotToolBehaviorProps> {

  waterAudio = waterAudio.clone();
  lastResult: ProcessStatus | null = null;
  laserSpriteFacet = create(SpriteGroupFacet, {
    children: [create(StaticSpriteFacet, { frame: LaserSprite, offset: [-82, -40] })],
  });

  update(delta: number, engine: Engine) {
    if (this.props.state.didDie || this.props.state.electrifiedEnd) {
      this.waterAudio.pause();
      this.props.armsSprite.props.frame = BotArmsSprite;
      this.props.sprite.transform.rotation = 0;
      return;
    }
    this.props.state.inventory.forEach(item => {
      if (item.type === BotToolChoice.Laser && item.count !== 1) {
        item.count = clamp(item.count + delta * 0.005, 1);
      }
    });
    if (!this.props.state.running) return;

    const { result } = this.props;
    if (result?.status !== 'running' || result.value.type !== 'command' || result.value.action !== 'use') {
      this.props.armsSprite.props.frame = BotArmsSprite;
      this.props.sprite.transform.rotation = 0;
      this.waterAudio.pause();
      this.props.toolSprite.props.children = emptyArray;
      if (result?.status === 'error' && this.lastResult?.status !== 'error') {
        errorSound.play();
      }
      this.lastResult = result;
      return;
    }
    switch (result.value.tool) {
      case BotToolChoice.WateringCan: {
        if (!result.value.enRoute) {
          this.props.armsSprite.props.frame = BotArmsWateringSprite;
          this.props.sprite.transform.rotation = -8;
          this.waterAudio.play();
        } else {
          this.props.armsSprite.props.frame = BotArmsSprite;
          this.props.sprite.transform.rotation = 0;
          this.waterAudio.pause();
        }
        break;
      }
      case BotToolChoice.Laser: {
        const { position: targetPos } = result.value.target.transform;
        const { position: entityPos } = this.entity.transform;
        const angle = getAngleRad(targetPos, entityPos);
        const isLeftOfTarget = targetPos[0] > entityPos[0];
        this.props.toolSprite.props.children = [this.laserSpriteFacet];
        this.laserSpriteFacet.transform.position[1] = -75;
        this.laserSpriteFacet.transform.rotation = (angle * (180 / Math.PI)) * (isLeftOfTarget ? -1 : 1);
        this.laserSpriteFacet.transform.scale[1] = isLeftOfTarget ? -1 : 1;
        if (result.value.enRoute) break;
        const laser = this.props.state.inventory.find(item => item.type === BotToolChoice.Laser);
        if (!laser) break;
        if (laser.count === 1) {
          const velocity: V2 = [-Math.cos(angle), -Math.sin(angle)];
          laser.count = 0;
          engine.add(
            create(BulletEntity, { damage: 25, velocity }),
            {
              transform: new Transform({
                position: addV2(
                  addV2([...entityPos], [0, -94]), // move up to gun Y
                  scaleV2([...velocity], 100), // move along gun axis to end of barrel
                ),
              }),
              zIndex: 1,
            },
          );
        }
        break;
      }
    }
    this.lastResult = result;
  }

  stop() {
    this.waterAudio.stop();
  }
}
